/**
 * Shared elements, constants, methods, and utility methods for PortfolioFilter and PipelineFilter
 */
import React from 'react';
import PropTypes from "prop-types";
import moment from 'moment';

/*****************************/
/*****  Shared Constants *****/
/*****************************/
export const dateDisplayFormat = 'MM/DD/YYYY';
export const dateISOFormat = 'YYYY-MM-DD';
export const today = moment();
export const yesterday = moment(today).subtract(1, 'd');
export const defaultStartDate = moment('02/22/2013', dateDisplayFormat);
export const dateRangePresets = [
  {
    label: "Today",
    range: { start: today, end: today },
    selected: false
  },
  {
    label: "Yesterday",
    range: { start: yesterday, end: yesterday },
    selected: false
  },
  {
    label: "Last 7 Days",
    range: { start: moment(today).subtract(7, 'd'), end: today },
    selected: false
  },
  {
    label: "Last 30 Days",
    range: { start: moment(today).subtract(30, 'd'), end: today },
    selected: false
  },
  {
    label: "Last 90 Days",
    range: { start: moment(today).subtract(90, 'd'), end: today },
    selected: false
  },
];
export const initialDateRange = { start: defaultStartDate, end: today };

/*****************************/
/*****  Shared Elements ******/
/*****************************/
/* eslint-disable react/no-multi-comp */
export const FilterHeaderHTML = (props) => (
  <div className="filter-header">
    <h2>Filter By</h2>
    <a className="filter-reset" onClick={props.resetAll}>clear filters</a>
  </div>
);
FilterHeaderHTML.propTypes = {
  resetAll: PropTypes.func.isRequired,
};

export const SectionHeader = (props) => (
  <div className="section-header">
    <span className="section-title">{props.title}</span>
  </div>
);
SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
};

export const SectionBody = (props) => {
  let itemRenderer;

  switch (props.controlType) {
    case 'checkbox':
    case 'radio':
      // eslint-disable-next-line react/display-name
      itemRenderer = item => <li key={item.label}><label className="control"><input type={props.controlType} onChange={() => props.toggleFunc(item)} checked={item.checked} /> {item.label}</label></li>;
      break;
    // istanbul ignore next
    default:
      break;
  }

  return (
    <div className="section-body">
      <ul className={props.title.toLowerCase()}>
        {props.state.map(itemRenderer)}
      </ul>
    </div>
  );
};
SectionBody.propTypes = {
  title: PropTypes.string.isRequired,
  controlType: PropTypes.string.isRequired, // 'checkbox', 'radio'
  state: PropTypes.array.isRequired,
  toggleFunc: PropTypes.func.isRequired,
};


/****************************/
/******  Shared Methods *****/
/****************************/
export function getDateRangeSectionHTML() {
  /**
   * convert date string from 'MM/DD/YYYY' to 'YYYY-MM-DD' which is the required format for HTML5 date input element
   * @param {Object} date a momentjs object
   */
  const formatDateToISOFormat = (date) => date.format(dateISOFormat);
  /**
   * @param {Object} range {start: momentJS object, end: momentJS object}
   */
  const isRangeAPreset = (range) => this.state.dateRangePresets.some((preset) => preset.start === range.start && preset.end === range.end);
  const editCustomRange = () => this.setState({shouldEditCustomDateRange: true, shouldSelectCustomDateRange: true});
  const cancelEditingCustomRange = () => {
    this.setState({
      selectedDateRange: this.state.currentDateRange,
      shouldEditCustomDateRange: false,
      shouldSelectCustomDateRange: !isRangeAPreset(this.state.currentDateRange)
    });
  };

  /**
   *
   * @param {String} label
   * @param {Object} date a momentjs object
   * @param {String} part 'START'|'END'
   */
  const customRangeControl = (label, date, part) => {
    /**
     * update `state.selectedDateRange`
     * @param {String} part 'START' | 'END'
     * @param {String} date ISO formatted date string 'YYYY-MM-DD'
     */
    const manualUpdateDateRange = (part, date) => {
      const dateRangePresets = this.state.dateRangePresets.map(preset => ({ ...preset, selected: false }));
      this.setState({ selectedDateRange: { ...this.state.selectedDateRange, [part.toLowerCase()]: moment(date) }, dateRangePresets });
    };
    // Support cancel editing with ESC key
    const keyUpHandler = (e) => {
      if (e.key === 'Escape'){
        cancelEditingCustomRange();
      }
    };
    return (
      <label className="control">
        <span className="label">{label}</span>
        <input
          type="date"
          value={formatDateToISOFormat(date)}
          onChange={(e) => manualUpdateDateRange(part, e.currentTarget.value)}
          onKeyUp={keyUpHandler}
        />
      </label>
    );
  };
  const customRangeControlFrom = customRangeControl('From', this.state.selectedDateRange.start, 'START');
  const customRangeControlTo = customRangeControl('To', this.state.selectedDateRange.end, 'END');

  const customDateRangeSectionHTML = (() => {
    if (this.state.shouldSelectCustomDateRange) {
      const style = {};
      if (this.state.shouldEditCustomDateRange) {
        style.form = {display: 'block'};
        style.readonlyText = {display: 'none'};
      } else {
        style.form = {display: 'none'};
        style.readonlyText = {display: 'flex'};
      }
      return (
        <div className="custom-date-range-container">
          <form onSubmit={(e) => {e.preventDefault(); this.applyFilters();}} style={style.form}>
            <div className="custom-date-range-inputs">
              {customRangeControlFrom}
              {customRangeControlTo}
            </div>
            <div className="actions">
              <button type="button" className="button is-success" onClick={cancelEditingCustomRange}>Cancel</button>
              <button type="submit" className="button is-primary hidden-sm-down">Apply</button>
            </div>
          </form>
          <div className="current-date-range" onClick={editCustomRange} style={style.readonlyText}>
            <span className="date-range">{this.state.currentDateRange.start.format(dateDisplayFormat)} - {this.state.currentDateRange.end.format(dateDisplayFormat)}</span>
          </div>
        </div>
      );
    }
  })();

  return (
    <div className="section">
      <div className="section-header">
        <span className="section-title">Date Range</span>
        <a className="btn-section-reset" onClick={this.resetDateRange}>show all</a>
      </div>
      <div className="section-body">
        <ul className="date-range-presets">
          {
            this.state.dateRangePresets.map((preset) => {
              const selected = preset.selected && !this.state.shouldSelectCustomDateRange;
              return <li key={preset.label}><a data-selected={selected} onClick={() => this.setDateRangeFromPreset(preset)}>{preset.label}</a></li>
            })
          }
          <li><a data-selected={this.state.shouldSelectCustomDateRange} onClick={() => !this.state.shouldSelectCustomDateRange && editCustomRange()}>Custom Date Range</a></li>
        </ul>
        { customDateRangeSectionHTML }
      </div>
    </div>
  );
}

export function resetDateRange(){
  const selectedDateRange = { start: defaultStartDate, end: today };
  const dateRangePresets = this.state.dateRangePresets.map(preset => ({ ...preset, selected: false }));
  this.setState({ selectedDateRange, dateRangePresets, shouldEditCustomDateRange: false, shouldSelectCustomDateRange: true }, () => {
    // istanbul ignore next
    if (shouldInitiateSearchImmediately.bind(this)()) {
      this.applyFilters();
    }
  });
}

/**
 * sets the date range section filter controls with preset
 * @param {Object} preset Refer to definition of `state.dateRangePresets` for shape of preset object
 */
export function setDateRangeFromPreset(preset){
  const dateRangePresets = this.state.dateRangePresets.map(
    (p) => p.label === preset.label ? { ...p, selected: true } : { ...p, selected: false }
  );
  const selectedDateRange = { start: preset.range.start, end: preset.range.end };

  this.setState(
    {
      selectedDateRange, dateRangePresets,
      shouldEditCustomDateRange: false,
      shouldSelectCustomDateRange: false
    },
    () => {
      // istanbul ignore next
      if (shouldInitiateSearchImmediately.bind(this)()) {
        this.applyFilters();
      }
    }
  );
}

export function applyFilters(){
  this.setState({
    currentDateRange: this.state.selectedDateRange,
    shouldEditCustomDateRange: false,
  });
  this.props.applyFilters();
}


/****************************/
/*****  Utility Methods *****/
/****************************/

/**
 * This is a helper function for the following desired behavior:
 * - On Desktop, a search is initiated immediately upon any of the filter changes (toggling any Status or Type).
 * - On Mobile, search is only initiated when user clicked on DONE to close the filters panel.
 *
 * Implementation Notes:
 *  Search is initiated immediately only on Desktop.
 *  The date range **Apply** button is different on Desktop and Mobile, so we
 *  are taking advantage of this difference to determine whether the user is on Desktop or Mobile.
 * @return {Boolean}
 */
export function shouldInitiateSearchImmediately() {
  const displayStyle = window.getComputedStyle(this.refBtnApplyFilters.current).display;

  return displayStyle === 'none';
}

/**
 * Reset filter section where all filters are represented as checkboxes.
 * @param {String} statename The corresponding state name to the section to be reset.
 * @param {Boolean} searchImmediately Optional. Should it initiate search immediately after setState?
 */
export function resetFilterSectionOfCheckboxVals(statename, searchImmediately = false) {
  const items = this.state[statename].map(i => ({ ...i, checked: true }));
  this.setState({ [statename]: items }, () => {
    if (searchImmediately) {
      this.applyFilters();
    }
  });
}

/**
 * @param {Object} item Refer to definition of PipelineFilter `state.types` or `state.statuses` for shape of item object
 * @param {String} statename The state the given item belongs to.
 */
export function toggleFilterItemOfCheckboxVal(item, statename) {
  const items = this.state[statename].map(i => i.label === item.label ? { ...i, checked: !i.checked } : i);
  this.setState({ [statename]: items }, () => {
    // istanbul ignore next
    if (shouldInitiateSearchImmediately.bind(this)()) {
      this.applyFilters();
    }
  });
}

/**
 *
 * @param {Object} item Refer to definition of PortfolioFilter `state.types` for shape of item object
 * @param {String} statename  The state the given item belongs to.
 */
export function toggleFilterItemOfRadioVal(item, statename) {
  const items = this.state[statename].map(i => i.label === item.label ? { ...i, checked: true } : { ...i, checked: false });
  this.setState({ [statename]: items }, () => {
    // istanbul ignore next
    if (shouldInitiateSearchImmediately.bind(this)()) {
      this.applyFilters();
    }
  });
}
